import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useWebsitesWithStatus } from './useWebsitesWithStatus';
import { useCreditBalances } from './useCreditBalances';
import { buildNotifications } from '../utils/notifications';

// Feeds the header bell. The RULES live in utils/notifications.js, pure and
// tested; this hook only gathers the inputs.
//
// It reuses useWebsitesWithStatus rather than re-deriving "is this site
// connected?", so the bell can never disagree with the Overview card and the
// Websites tab about which sites need attention.
//
// COST. The header is on every page, so this deliberately does not poll hard.
// It loads once, refreshes on an explicit `refresh()` (the panel calls it when
// opened, so acting on a notification updates the list behind it), and
// otherwise re-checks only on a slow timer while the tab is visible. Every
// fetch failure is swallowed: a bell that cannot load is an empty bell, never
// an error surfaced over whatever the user was actually doing.

const POLL_MS = 180000; // 3 minutes — these are slow-moving conditions

//  comes from the caller rather than a second fetch: Dashboard
// already holds userData for the header badge, and a duplicate read is a second
// chance for the bell and the badge to disagree about the same trial.
export function useNotifications(userId, { trialEndsAt = null } = {}) {
  const [rawWebsites, setRawWebsites] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [tickets, setTickets] = useState([]);

  const { websites, statusReady } = useWebsitesWithStatus(userId, rawWebsites);
  // `lots` is what carries expiry dates; `detail` is the pooled compatibility
  // shim and reading it for a credit total triples the number (see
  // notifications.js). No extra request — the hook is already mounted.
  const { detail: creditDetail, lots } = useCreditBalances(userId);

  const refresh = useCallback(async () => {
    if (!userId) return;
    // Independent: one failing source must not blank the other two.
    const [sites, sub, tks] = await Promise.allSettled([
      api.getUserWebsites(userId),
      api.getUserSubscription(userId),
      api.getUserTickets(userId, { limit: 50 }),
    ]);

    if (sites.status === 'fulfilled') setRawWebsites(sites.value?.data || []);
    if (sub.status === 'fulfilled') setSubscription(sub.value?.data || null);
    if (tks.status === 'fulfilled') setTickets(tks.value?.data || []);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const run = () => { if (!cancelled) refresh(); };

    run();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, POLL_MS);

    return () => { cancelled = true; clearInterval(id); };
  }, [userId, refresh]);

  const notifications = buildNotifications({
    websites,
    statusReady,
    lots,
    creditDetail,
    periodEnd: subscription?.next_billing_date || null,
    tickets,
    subscription,
    trialEndsAt,
  });

  return { notifications, count: notifications.length, refresh };
}

export default useNotifications;
